import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, Stack } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FileInputControl, Form } from '@/core/components';
import { CustomSelect } from '@/core/components/Select';

import { RoleType } from '@feature/security';
import { Author } from '@feature/utils';

import { ProofUploadRequest } from './ProofTypes';

interface ProofFileUploadModalProps {
  onClose: () => void;
  isSubmitting: boolean;
  onSubmit: (request: ProofUploadRequest) => void;
  isEditing?: boolean;
  existingUserId?: number;
  currentUserRole: RoleType;
  currentUserId: number;
  authors?: Author[];
}

const schema = z.object({
  file: z.instanceof(File),
  userId: z.number(),
});

type ProofFileUploadSchema = z.infer<typeof schema>;

export function ProofFileUploadModal({
  onClose,
  isSubmitting,
  onSubmit,
  isEditing = false,
  existingUserId,
  currentUserRole,
  currentUserId,
  authors = [],
}: ProofFileUploadModalProps) {
  const { t } = useTranslation();

  const form = useForm<ProofFileUploadSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      userId: currentUserRole === 'AUTHOR' ? currentUserId : existingUserId || undefined,
    },
  });

  const handleSubmit = (values: ProofFileUploadSchema) => {
    const request: ProofUploadRequest = {
      file: values.file,
      userId: values.userId,
    };

    onSubmit(request);
  };

  const authorOptions = authors
    .filter(author => author.reclaimed)
    .map(author => ({
      value: author.user?.id.toString() || '',
      label: author.userName,
    }));

  return (
    <Modal
      opened
      centered
      onClose={onClose}
      title={isEditing ? t('proof.edit.title') : t('proof.upload.title')}
      size="md"
    >
      <Form
        onSubmit={form.handleSubmit(handleSubmit)}
        onCancel={onClose}
        title=""
        isSubmitting={isSubmitting}
      >
        <Stack gap="xl">
          <Stack gap="md">
            <FileInputControl
              name="file"
              label={t('proof.upload.file')}
              isSubmitting={isSubmitting}
              withAsterisk
              accept="application/pdf,image/png,image/jpeg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              control={form.control}
            />
          </Stack>

          {!(isEditing || currentUserRole === 'AUTHOR') && (
            <Stack gap="md">
              <CustomSelect
                name="userId"
                label={t('proof.owner')}
                placeholder={t('proof.owner')}
                data={authorOptions}
                value={form.watch('userId')?.toString() || null}
                onChange={(value: string | null) => form.setValue('userId', value ? parseInt(value) : 0)}
                withAsterisk
                disabled={isSubmitting}
                searchable
              />
            </Stack>
          )}
        </Stack>
      </Form>
    </Modal>
  );
}
